import React, {useState} from 'react';
import TopBar from '../../../Components/TopBar';
import Page from '../../../Components/Containers/Page';
import { useVideos } from '../../../Data/Help';
import { PageLoading } from '../../../Components/Loading';
import VideoPlayer from './VideoPlayer';
import { classNames, deviceIsMd } from '../../../Services/helpers';
import { useOnboardingJourney } from '../../../Data/Onboarding';
import Icon from '../../../Components/Icons/Icon';
import { useMobile } from '../../../Components/Mobile';
import { usePreference } from '../../../Data/Preference';

const Videos = () => {
    const {videos} = useVideos();
    const {journey, markVideoAsPlayed, markVideoAsUnplayed, refresh} = useOnboardingJourney(true);

    return <>
        <TopBar title="Videos" left="BackBtn" />
        <Page>
            {videos ? <Inner {...{videos, journey, markVideoAsPlayed, markVideoAsUnplayed, refresh}} /> : <PageLoading />}
        </Page>
    </>
}

const Inner = ({videos, journey, markVideoAsPlayed, markVideoAsUnplayed, refresh}) => {
    const [selected, setSelected] = useState(null);
    const [unwatched, setUnwatched] = usePreference('videoFilter:unwatched', false);
    // const [customers, setCustomers] = usePreference('videoFilter:customers', false);
    // const [invoices, setInvoices] = usePreference('videoFilter:invoices', false);
    // const [intro, setIntro] = usePreference('videoFilter:intro', false);
    // const [scheduling, setScheduling] = usePreference('videoFilter:scheduling', false);
    // const [services, setServices] = usePreference('videoFilter:services', false);

    const filter = (video) => {
        return (unwatched ? !journey?.playedVideos?.includes(video.key) : true);
                // (customers ? video.tags.includes('Customers') : true) &&
                // (invoices ? video.tags.includes('Invoices') : true) &&
                // (intro ? video.tags.includes('Intro') : true) &&
                // (scheduling ? video.tags.includes('Scheduling') : true) &&
                // (services ? video.tags.includes('Services') : true);
    }
    const filtered = videos.filter(filter);

    return <>
        <div className="flex flex-row flex-wrap justify-center items-center mb-4 gap-x-4 gap-y-2">
            <Toggle label="Unwatched" toggled={unwatched} setToggled={setUnwatched} />
            {/*
            <Toggle label="Customers" toggled={customers} setToggled={setCustomers} />
            <Toggle label="Invoices" toggled={invoices} setToggled={setInvoices} />
            <Toggle label="Intro" toggled={intro} setToggled={setIntro} />
            <Toggle label="Scheduling" toggled={scheduling} setToggled={setScheduling} />
            <Toggle label="Services" toggled={services} setToggled={setServices} />
             */}
        </div>
        <div className="flex flex-row flex-wrap gap-4 mb-8 justify-between">
            {filtered.map(video => {
                return <Video
                    key={video.id}
                    {...{
                        video,
                        isSelected: video.id === selected,
                        setSelected,
                        watched: journey?.playedVideos?.includes(video.key) ?? false,
                        markVideoAsPlayed,
                        markVideoAsUnplayed,
                        refresh,
                    }}
                />
            })}
        </div>
        {filtered.length === 0 && <p className="my-5 text-center text-muted text-sm">
            No videos to show
        </p>}
    </>
}

const Toggle = ({label, toggled, setToggled}) => {
    return <div className={classNames(
            'px-2 py-1 flex flex-row items-center justify-between border rounded-full cursor-pointer text-sm',
            toggled ? 'border-blue-900 bg-brand text-white fill-white' : 'border-slate-300 bg-slate-100',
        )}
        onClick={() => setToggled(!toggled)}
    >
        {label}
        &nbsp;
        <Icon icon={toggled ? 'Check' : 'XMark'} theme="solid" size="0.5x" />
    </div>
}

const Video = ({video, isSelected, setSelected, watched, markVideoAsPlayed, markVideoAsUnplayed, refresh}) => {
    const {isMobile} = useMobile();
    const playsFullscreen = isMobile && window.document.fullscreenEnabled;

    const transitionClasses = 'transition-all duration-300 ease-in-out';
    return <div className={classNames(
            'w-full lg:w-[48%]',
            'relative w-full border border-slate-300 bg-slate-100 text-slate-900 rounded-lg overflow-hidden flex flex-row',
            transitionClasses,
            (isSelected && !playsFullscreen) ? 'justify-center lg:w-full' : 'justify-between items-center',
        )}
        onClick={() => setSelected(isSelected ? null : video.id)}
    >
        <div className={classNames(
            'absolute top-0 right-0 p-2 rounded-full z-30',
            transitionClasses,
            (isSelected && !playsFullscreen) ? 'opacity-100' : 'opacity-0',
        )}>
            <Icon icon="XMark" className="fill-slate-900" />
        </div>

        <div className={classNames(
            'shrink-0 relative transition-all duration-300 ease-in-out',
            'flex flex-col items-center justify-center lg:m-2',
            (isSelected && !playsFullscreen) ? 'md:w-[50%] w-[80%] h-full' : 'md:w-[30%] h-full',
        )}>
            <img src={deviceIsMd() ? video.posters.medium : video.posters.small}
                alt={video.title}
                className={classNames(
                    'w-full h-auto lg:rounded-lg',
                    transitionClasses,
                    // (isSelected && !playsFullscreen) ? 'opacity-0 pointer-events-none' : 'opacity-100',
                    isSelected ? 'opacity-0 pointer-events-none' : 'opacity-100',
                )}
                height={video.dimensions.height}
                width={video.dimensions.width}
            />

            <div className={classNames(
                !playsFullscreen && 'top-0 right-0 w-full h-auto',
                playsFullscreen && 'top-0 left-0 w-dvw h-dvh z-50',
                isSelected ? (playsFullscreen ? 'fixed' : 'absolute') : 'hidden',
                isSelected ? 'opacity-100 border' : 'opacity-0 pointer-events-none'
            )}>
                <VideoPlayer
                    key={video.id}
                    video={video}
                    className="h-full w-auto"
                    options={{
                        preload:  isSelected ? 'auto' : 'metadata',
                        autoplay: isSelected,
                        fluid: true,
                        controls: true,
                        playsFullscreen,
                    }}
                    isOpen={isSelected}
                    onPlayed={() => markVideoAsPlayed(video.key)}
                    onFinished={() => setSelected(null)}
                />
            </div>

        </div>

        <div className={classNames(
            'grow flex flex-col items-start',
            transitionClasses,
            (isSelected && !playsFullscreen) ? 'max-w-[0px] opacity-0' : 'max-w-[1000px] opacity-100',
        )}>
            <div className="m-4 prose prose-sm prose-slate">
                <h3 className="mb-2 font-bold flex flex-row items-center">
                    {video.title}
                </h3>
                <p className="text-sm text-balance">{video.description}</p>
                <p className="text-xs text-muted flex flex-row items-center">
                    {video.duration.substring(4)}
                    <WatchedToggle {...{video, watched, markVideoAsPlayed, markVideoAsUnplayed, refresh}} />
                </p>
                {/*
                <p className="text-xs text-muted">
                    {video.tags.map(tag => {
                        return <span className="mr-2 px-2 py-1 border border-slate-400 rounded-full">
                            {tag}
                        </span>
                    })}
                </p>
                */}
            </div>
        </div>
    </div>
}

const WatchedToggle = ({video, watched, markVideoAsPlayed, markVideoAsUnplayed}) => {
    const [toggledWatched, setToggledWatched] = useState(null);

    const doToggle = async () => {
        if (watched) {
            setToggledWatched(false);
            await markVideoAsUnplayed(video.key);
        } else {
            setToggledWatched(true);
            await markVideoAsPlayed(video.key);
        }
    }

    return <div className="inline-block ml-3 mt-1">
        <Icon icon="CircleCheck"
            theme={(toggledWatched ?? watched) ? 'solid' : 'light'}
            className={(toggledWatched ?? watched) ? 'fill-brand' : 'fill-muted'}
            onClick={doToggle}
        />
    </div>
}

export default Videos;